import { IStairCalculationResult, IStairConfig } from '@stair-craft/common/util';

import { FRONT_VIEW_OVERHANG_MM, projectFrontView } from './front-view-projection';
import { declutterLabels, placeNotes } from './label-declutter';
import { projectLandingScheme } from './landing-scheme-projection';
import {
    EPrimitiveKind,
    ETextAlign,
    ETextBaseline,
    ETextRole,
    FONT_MONO_FAMILY,
    IFont,
    IPaneRect,
    IPlanPrimitive,
    IProjectionOutput,
    ITextPrimitive,
    IViewFit,
} from './plan-primitive';
import { IPxBounds, projectionBounds, translateProjection } from './projection-bounds';
import { projectSideView } from './side-view-projection';
import { projectSpiralRotation } from './spiral-rotation-projection';
import { projectSpiralStepDetail } from './spiral-step-detail-projection';
import { projectStepDetail } from './step-detail-projection';
import { projectStepsSchema } from './steps-schema-projection';
import { projectStringer } from './stringer-projection';
import { ITextMeasurer } from './text-measurer';
import { IPlanBox, IPlanOffset, TOP_PLAN_PADS, focusBBox, projectTopPlan, stairOffset } from './top-plan-projection';
import { projectWinderCombined } from './winder-combined-projection';
import { projectWinderNumbered } from './winder-numbered-projection';

/*
 * The sheet follows the engineering-drawing practice of ГОСТ 2.305 / ISO 128 (first-angle
 * projection): the front view is the main view, the top view stands directly below it and the
 * side view directly to its right. The three share one standard scale (ГОСТ 2.302), the top view
 * shares the front view's X axis and the side view the front view's floor line. The detail views
 * (templates, stringer, schemes) stand apart in a regular grid of equal cells, each at its own
 * standard scale. Every view carries a title row with its scale; one thin frame bounds the sheet.
 *
 * That full arrangement is the DXF file. The screen «План» position keeps the top view and the
 * detail cells only: the «Фасады» position already shows the front and side view, and the top
 * view takes the room they leave, often at a larger standard scale.
 */

/**
 * The one sheet everything is laid out on, CSS pixels — the screen shows it scaled into the
 * canvas, the DXF writes it in millimetres. The screen size never changes it.
 */
export const SHEET_SIZE: { width: number; height: number } = { width: 1600, height: 1200 };
/** Gap between the canvas edge and the sheet frame, pixels. */
const SHEET_MARGIN: number = 8;
/** Gap between the sheet frame and the content inside it, pixels. */
const FRAME_PAD: number = 10;
/** Height of the title row every view carries above its drawing, pixels. */
export const SHEET_TITLE_BAND: number = 22;
/** The font of a view title on the sheet. */
export const SHEET_TITLE_FONT: IFont = { weight: 500, size: 10, family: FONT_MONO_FAMILY };
/** Gap between two views of the projection group, pixels. */
const VIEW_GAP: number = 24;
/** The gap under each parameter row over the top view, pixels. */
const TOP_NOTE_ROW_GAP: number = 3;
/** Gap between the projection group and the detail zone, pixels. */
const ZONE_GAP: number = 16;
/** Gap between two detail cells, pixels. */
const CELL_GAP: number = 8;
/** The smallest detail cell the sheet accepts, title row included. */
const MIN_CELL: { w: number; h: number } = { w: 220, h: 180 };
/** Detail cells aim at this aspect: a 4 : 3 box is what the templates and schemes fill best. */
const CELL_ASPECT: { w: number; h: number } = { w: 4, h: 3 };
/** CSS pixels in a millimetre: a CSS pixel is 1/96 inch. */
const CSS_PX_PER_MM: number = 96 / 25.4;
/** Reduction scales of ГОСТ 2.302 — the denominators N of «1 : N», small to large. */
const STANDARD_DENOMINATORS: readonly number[] = [1, 2, 2.5, 4, 5, 10, 15, 20, 25, 40, 50, 75, 100, 200, 400, 500, 800, 1000];
/** The projection group is never drawn larger than 1 : 10 — beyond that the whole stair is a detail. */
const GROUP_MIN_DENOMINATOR: number = 10;
/** How many fitting group scales, from the largest down, the sheet weighs against each other. */
const SCALE_LOOKAHEAD: number = 3;
/** Steps of the search for the pane size that gives a detail its standard scale. */
const DETAIL_FIT_STEPS: number = 22;
/** How far a detail's fit may miss its standard scale and still be labelled with it. */
const SCALE_TOLERANCE: number = 0.005;
/** Nominal pane handed to a projection that takes its scale and origin from the sheet. */
const NOMINAL_PANE: IPaneRect = { x: 0, y: 0, w: 1000, h: 1000 };

/** Pane identifier — it is also the DXF layer name, so the values stay English. */
export enum EPaneId {
    SideView = 'SideView',
    TopView = 'TopView',
    FrontView = 'FrontView',
    Stringer = 'Stringer',
    StepsSchema = 'StepsSchema',
    StepDetail = 'StepDetail',
    LandingScheme = 'LandingScheme',
    WinderCombined = 'WinderCombined',
    WinderNumbered = 'WinderNumbered',
    SpiralRotation = 'SpiralRotation',
}

/** Which views of the projection group a sheet carries. */
export enum ESheetViews {
    /** Front, top and side view — the full drawing the DXF file keeps. */
    All = 'all',
    /** The top view only — the screen «План» position; «Фасады» shows the front and side view. */
    TopOnly = 'top-only',
}

/** Which part of the sheet a pane belongs to. */
export enum EPaneRole {
    /** A view of the projection group: one shared scale, aligned with its neighbours. */
    Projection = 'projection',
    /** A detail view in the grid: its own scale, its own cell. */
    Detail = 'detail',
}

/** One pane of the sheet: where it sits, what it is called and what it draws. */
export interface IPane {
    id: EPaneId;
    title: string;
    /** «М 1:50» — the standard scale the pane is drawn at; null for a scheme without one. */
    scale: string | null;
    role: EPaneRole;
    /** The pane on the canvas, its title row included; the drawing is clipped to it. */
    rect: IPaneRect;
    projection: IProjectionOutput;
}

/** The sheet as data: its frame, its panes and the zone the detail cells fill. */
export interface ISheet {
    frame: IPaneRect;
    panes: IPane[];
    detailZone: IPaneRect | null;
}

/** A detail view before its cell is known: the grid places it, `project` fills it. */
interface IDetailSpec {
    id: EPaneId;
    project: (rect: IPaneRect) => IProjectionOutput;
}

/** A view of the projection group: it draws at the scale and origin point the sheet gives. */
interface IGroupView {
    id: EPaneId;
    project: (fit: IViewFit) => IProjectionOutput;
}

/** The three views of the projection group and what ties the front view to the top view. */
interface IProjectionGroup {
    front: IGroupView;
    side: IGroupView;
    top: IGroupView;
    /** The room X (mm) of the front view's millimetre origin — the top view's origin is room X 0. */
    frontRoomX: number;
    /** What the top view shows, room millimetres. */
    focus: IPlanBox;
    /** Which of the three views the sheet lays out. */
    views: ESheetViews;
}

/** A view placed on the sheet: its pane and the projection drawn in it. */
interface IPlacedView {
    id: EPaneId;
    rect: IPaneRect;
    projection: IProjectionOutput;
}

/** The front and side views on one floor line. */
interface IPlacedRow {
    front: IPlacedView;
    side: IPlacedView;
    top: number;
    bottom: number;
}

/** The projection group laid out at one scale, in coordinates of its own top-left corner. */
interface IGroupPlan {
    width: number;
    height: number;
    views: IPlacedView[];
    /** Bottom of the front/side row. */
    rowBottom: number;
    /** Right edge of the top view. */
    topRight: number;
}

/** A regular grid of detail cells. */
interface IDetailGrid {
    cols: number;
    cellW: number;
    cellH: number;
    /** The cells the details take, in reading order. */
    cells: IPaneRect[];
}

/** One way to share the sheet between the group and the details. */
interface ISheetCandidate {
    groupAt: { x: number; y: number };
    zone: IPaneRect;
    grid: IDetailGrid;
}

/**
 * Pane titles that depend on nothing but the pane id — one `$localize` each, read at
 * draw time so the locale is already in place.
 */
const FIXED_PANE_TITLES: Record<Exclude<EPaneId, EPaneId.Stringer>, () => string> = {
    [EPaneId.SideView]: () => $localize`:@@planPaneSide:вид сбоку`,
    [EPaneId.TopView]: () => $localize`:@@planPaneTop:вид сверху`,
    [EPaneId.FrontView]: () => $localize`:@@planLabelFront:вид спереди`,
    [EPaneId.StepsSchema]: () => $localize`:@@planLabelStepsSchema:схема ступеней`,
    [EPaneId.StepDetail]: () => $localize`:@@planLabelStepDrawing:чертёж ступеней`,
    [EPaneId.LandingScheme]: () => $localize`:@@planLabelLandingScheme:схема площадки`,
    [EPaneId.WinderCombined]: () => $localize`:@@planLabelWinderCombined:схема забежных ступеней`,
    [EPaneId.WinderNumbered]: () => $localize`:@@planLabelWinderNumbered:забежные ступени`,
    [EPaneId.SpiralRotation]: () => $localize`:@@planLabelSpiralRotation:угол поворота`,
};

/** The pane title shown on the sheet; the DXF writer takes its titles from the same call. */
function paneTitle(id: EPaneId, config: IStairConfig): string {
    if (id === EPaneId.Stringer) {
        return config.stringerType === 'kosour'
            ? $localize`:@@planLabelKosour:чертёж косоура`
            : $localize`:@@planLabelTetiva:чертёж тетивы`;
    }
    return FIXED_PANE_TITLES[id]();
}

/** «М 1:50» for the denominator 50. */
export function scaleText(denominator: number): string {
    const prefix: string = $localize`:@@planPaneScale:М`;
    return `${prefix} 1:${String(denominator)}`;
}

/** The pixels a millimetre takes at «1 : N» on a CSS-pixel canvas. */
function pxPerMmOf(denominator: number): number {
    return CSS_PX_PER_MM / denominator;
}

/** The largest standard scale not larger than `pxPerMm`; past 1 : 1000 the next round thousand. */
export function standardDenominator(pxPerMm: number): number {
    const exact: number = CSS_PX_PER_MM / pxPerMm;
    const found: number | undefined = STANDARD_DENOMINATORS.find((denominator: number) => denominator >= exact * (1 - 1e-9));
    return found ?? Math.ceil(exact / 1000) * 1000;
}

/** The width a title row needs: the title, two spaces, the scale. */
function titleWidth(title: string, scale: string | null, measurer: ITextMeasurer): number {
    const label: string = scale === null ? title.toUpperCase() : `${title.toUpperCase()}  ${scale}`;
    return measurer.measure(label, SHEET_TITLE_FONT) + 8;
}

// ─── The projection group ─────────────────────────────────

/** The room X of the front view's millimetre origin, per stair type. */
function frontRoomXOf(result: IStairCalculationResult, config: IStairConfig): number {
    const offset: IPlanOffset = stairOffset(result, config);
    if (config.type === 'straight') {
        return offset.x + (result.geometry.steps.at(0)?.position.x ?? 0) - FRONT_VIEW_OVERHANG_MM;
    }
    if (config.type === 'spiral') {
        return offset.x + (config.spiralDiameter ?? 1600) / 2;
    }
    // The L/U front view's origin is the stair's own X 0.
    return offset.x;
}

/** Front, side and top view of this stair type, and the room X of the front view's origin. */
function projectionGroup(
    result: IStairCalculationResult,
    config: IStairConfig,
    measurer: ITextMeasurer,
    { sideOnly, views }: { sideOnly: boolean; views: ESheetViews }
): IProjectionGroup {
    return {
        views,
        front: {
            id: EPaneId.FrontView,
            project: (fit: IViewFit): IProjectionOutput =>
                config.type === 'straight'
                    ? projectFrontView(result, config, NOMINAL_PANE, measurer, fit)
                    : projectSideView(result, config, NOMINAL_PANE, measurer, { axis: 'z', fit }),
        },
        side: {
            id: EPaneId.SideView,
            project: (fit: IViewFit): IProjectionOutput =>
                projectSideView(result, config, NOMINAL_PANE, measurer, { axis: 'x', sideOnly, fit }),
        },
        top: {
            id: EPaneId.TopView,
            project: (fit: IViewFit): IProjectionOutput => projectTopPlan(result, config, NOMINAL_PANE, measurer, fit),
        },
        frontRoomX: frontRoomXOf(result, config),
        focus: focusBBox(result, config),
    };
}

/** A view drawn with its origin at (0, 0) and the box it covers; an empty view covers the origin only. */
function probe(view: IGroupView, pxPerMm: number, measurer: ITextMeasurer): { output: IProjectionOutput; box: IPxBounds } {
    // Labels are kept apart before the box is taken, so the pane is cut to where they ended up.
    // Notes go above the drawing and level labels beside it first, out of the drawing's way.
    const drawn: IProjectionOutput = placeNotes(view.project({ pxPerMm, origin: { x: 0, y: 0 } }), measurer);
    const output: IProjectionOutput = declutterLabels(drawn, measurer, null);
    return { output, box: projectionBounds(output, measurer) ?? { minX: 0, minY: 0, maxX: 0, maxY: 0 } };
}

/** A probed view moved into its pane: `originX/Y` is where its origin goes in sheet coordinates. */
function place(id: EPaneId, output: IProjectionOutput, rect: IPaneRect, originX: number, originY: number): IPlacedView {
    return { id, rect, projection: translateProjection(output, originX - rect.x, originY - rect.y) };
}

/** The front and side views on one floor line; the floor is at y 0, the front origin at `frontX`. */
function planRow(
    group: IProjectionGroup,
    pxPerMm: number,
    titles: { front: number; side: number },
    frontX: number,
    measurer: ITextMeasurer
): IPlacedRow {
    const front: { output: IProjectionOutput; box: IPxBounds } = probe(group.front, pxPerMm, measurer);
    const side: { output: IProjectionOutput; box: IPxBounds } = probe(group.side, pxPerMm, measurer);
    const top: number = Math.min(front.box.minY, side.box.minY) - SHEET_TITLE_BAND;
    const bottom: number = Math.max(front.box.maxY, side.box.maxY);

    const frontRect: IPaneRect = {
        x: frontX + front.box.minX,
        y: top,
        w: Math.max(front.box.maxX - front.box.minX, titles.front),
        h: bottom - top,
    };
    const sideRect: IPaneRect = {
        x: frontRect.x + frontRect.w + VIEW_GAP,
        y: top,
        w: Math.max(side.box.maxX - side.box.minX, titles.side),
        h: bottom - top,
    };
    return {
        front: place(group.front.id, front.output, frontRect, frontX, 0),
        side: place(group.side.id, side.output, sideRect, sideRect.x - side.box.minX, 0),
        top,
        bottom,
    };
}

function shiftView(view: IPlacedView, dx: number, dy: number): IPlacedView {
    return { id: view.id, rect: { ...view.rect, x: view.rect.x + dx, y: view.rect.y + dy }, projection: view.projection };
}

/**
 * The top view cut to its focus box, its title row starting at `y`. Room X 0 is at x 0, so a
 * front view placed by `frontRoomX` stands over it on the same X.
 */
function placeTopView(group: IProjectionGroup, pxPerMm: number, y: number, titleW: number, measurer: ITextMeasurer): IPlacedView {
    const focus: IPlanBox = group.focus;
    const box: IPxBounds = {
        minX: focus.x * pxPerMm - TOP_PLAN_PADS.left,
        maxX: (focus.x + focus.w) * pxPerMm + TOP_PLAN_PADS.right,
        minY: focus.z * pxPerMm - TOP_PLAN_PADS.top,
        maxY: (focus.z + focus.l) * pxPerMm + TOP_PLAN_PADS.bottom,
    };
    // The parameter rows stand over the focus box, one under another, in the order they were drawn.
    const drawn: IProjectionOutput = group.top.project({ pxPerMm, origin: { x: 0, y: 0 } });
    const notes: ITextPrimitive[] = drawn.primitives
        .filter((prim: IPlanPrimitive): prim is ITextPrimitive => prim.kind === EPrimitiveKind.Text && prim.role === ETextRole.Note)
        .sort((a: ITextPrimitive, b: ITextPrimitive) => a.at[1] - b.at[1]);
    const noteSet: Set<IPlanPrimitive> = new Set<IPlanPrimitive>(notes);
    const notesH: number = notes.reduce((sum: number, noteText: ITextPrimitive) => sum + noteText.font.size + TOP_NOTE_ROW_GAP, 0);
    const placedNotes: ITextPrimitive[] = notes.map((noteText: ITextPrimitive, i: number) => ({
        ...noteText,
        at: [box.minX, box.minY - notesH + i * (noteText.font.size + TOP_NOTE_ROW_GAP)],
        align: ETextAlign.Left,
        baseline: ETextBaseline.Top,
    }));
    const notesWidth: number = Math.max(
        0,
        ...placedNotes.map((noteText: ITextPrimitive) => measurer.measure(noteText.text, noteText.font))
    );
    const topRect: IPaneRect = {
        y,
        x: box.minX,
        w: Math.max(box.maxX - box.minX, titleW, notesWidth),
        h: SHEET_TITLE_BAND + notesH + box.maxY - box.minY,
    };
    const topOriginY: number = topRect.y + SHEET_TITLE_BAND + notesH - box.minY;
    // The top view is cut to its focus box; its labels are kept apart inside it.
    const drawing: IProjectionOutput = {
        ...drawn,
        primitives: drawn.primitives.filter((prim: IPlanPrimitive) => !noteSet.has(prim)),
    };
    const topOutput: IProjectionOutput = declutterLabels(drawing, measurer, box);
    return place(group.top.id, { ...topOutput, primitives: [...topOutput.primitives, ...placedNotes] }, topRect, 0, topOriginY);
}

/** The views of a plan moved to their own top-left corner. */
function groupPlanOf(views: IPlacedView[], rowBottom: number, top: IPlacedView): IGroupPlan {
    const left: number = Math.min(...views.map((view: IPlacedView) => view.rect.x));
    const right: number = Math.max(...views.map((view: IPlacedView) => view.rect.x + view.rect.w));
    const upper: number = Math.min(...views.map((view: IPlacedView) => view.rect.y));
    return {
        width: right - left,
        height: top.rect.y + top.rect.h - upper,
        views: views.map((view: IPlacedView) => shiftView(view, -left, -upper)),
        rowBottom: rowBottom - upper,
        topRight: top.rect.x + top.rect.w - left,
    };
}

/**
 * The projection group at one scale. With all three views: the front/side row on the floor line,
 * the top view under the front view sharing its room X. With the top view only (the screen «План»
 * position, where «Фасады» already shows the elevations): the top view alone. Room X 0 is at x 0
 * while placing; the result is moved to its own top-left corner.
 */
function planGroup(group: IProjectionGroup, denominator: number, config: IStairConfig, measurer: ITextMeasurer): IGroupPlan {
    const pxPerMm: number = pxPerMmOf(denominator);
    const scale: string = scaleText(denominator);
    const titleOf: (id: EPaneId) => number = (id: EPaneId): number => titleWidth(paneTitle(id, config), scale, measurer);
    if (group.views === ESheetViews.TopOnly) {
        const alone: IPlacedView = placeTopView(group, pxPerMm, 0, titleOf(EPaneId.TopView), measurer);
        // No row above it: the quadrant right of the top view starts at its top edge.
        return groupPlanOf([alone], -ZONE_GAP, alone);
    }
    const row: IPlacedRow = planRow(
        group,
        pxPerMm,
        { front: titleOf(EPaneId.FrontView), side: titleOf(EPaneId.SideView) },
        group.frontRoomX * pxPerMm,
        measurer
    );
    const top: IPlacedView = placeTopView(group, pxPerMm, row.bottom + VIEW_GAP, titleOf(EPaneId.TopView), measurer);
    return groupPlanOf([row.front, row.side, top], row.bottom, top);
}

// ─── The detail grid ──────────────────────────────────────

function gridSize(grid: IDetailGrid): number {
    return Math.min(grid.cellW / CELL_ASPECT.w, (grid.cellH - SHEET_TITLE_BAND) / CELL_ASPECT.h);
}

/** The cells of a cols × rows grid in `zone`, in reading order. */
function gridCells(zone: IPaneRect, size: { cols: number; rows: number; cellW: number; cellH: number }): IPaneRect[] {
    const cells: IPaneRect[] = [];
    for (let row: number = 0; row < size.rows; row++) {
        for (let col: number = 0; col < size.cols; col++) {
            cells.push({
                x: Math.round(zone.x + col * (size.cellW + CELL_GAP)),
                y: Math.round(zone.y + row * (size.cellH + CELL_GAP)),
                w: Math.max(1, Math.floor(size.cellW)),
                h: Math.max(1, Math.floor(size.cellH)),
            });
        }
    }
    return cells;
}

/** The grid of `count` equal cells that gives every cell the largest 4 : 3 box in `zone`. */
function bestGrid(zone: IPaneRect, count: number): IDetailGrid {
    let best: IDetailGrid = { cols: 1, cellW: 0, cellH: 0, cells: [] };
    for (let cols: number = 1; cols <= count; cols++) {
        const rows: number = Math.ceil(count / cols);
        const cellW: number = (zone.w - (cols - 1) * CELL_GAP) / cols;
        const cellH: number = (zone.h - (rows - 1) * CELL_GAP) / rows;
        if (cellW <= 0 || cellH <= 0) {
            continue;
        }
        const grid: IDetailGrid = { cols, cellW, cellH, cells: gridCells(zone, { cols, rows, cellW, cellH }).slice(0, count) };
        if (best.cells.length === 0 || gridSize(grid) > gridSize(best)) {
            best = grid;
        }
    }
    return best;
}

function gridFits(grid: IDetailGrid): boolean {
    return grid.cells.length > 0 && grid.cellW >= MIN_CELL.w && grid.cellH >= MIN_CELL.h;
}

function zoneOf(x: number, y: number, right: number, bottom: number): IPaneRect {
    return { x, y, w: Math.max(0, right - x), h: Math.max(0, bottom - y) };
}

/**
 * The three ways to give the details their zone at one group plan: the free quadrant under the
 * side view and right of the top view, a band below the group, a band right of it.
 */
function candidates(plan: IGroupPlan, content: IPaneRect, count: number): ISheetCandidate[] {
    const right: number = content.x + content.w;
    const bottom: number = content.y + content.h;
    const zones: { groupAt: { x: number; y: number }; zone: IPaneRect }[] = [
        {
            groupAt: { x: content.x, y: content.y },
            zone: zoneOf(content.x + plan.topRight + ZONE_GAP, content.y + plan.rowBottom + ZONE_GAP, right, bottom),
        },
        {
            groupAt: { x: content.x + (content.w - plan.width) / 2, y: content.y },
            zone: zoneOf(content.x, content.y + plan.height + ZONE_GAP, right, bottom),
        },
        {
            groupAt: { x: content.x, y: content.y + (content.h - plan.height) / 2 },
            zone: zoneOf(content.x + plan.width + ZONE_GAP, content.y, right, bottom),
        },
    ];
    return zones.map(({ groupAt, zone }: { groupAt: { x: number; y: number }; zone: IPaneRect }) => ({
        grid: bestGrid(zone, count),
        groupAt,
        zone,
    }));
}

/**
 * The largest pane share k (0..1) whose fit stays at or under `target`, with its drawing; null
 * when none is found. A pane too small to draw in reports no fit or a fit at or below zero, and
 * the search grows it.
 */
function searchStandardFit(at: (k: number) => IProjectionOutput, target: number): { output: IProjectionOutput; k: number } | null {
    let low: number = 0;
    let high: number = 1;
    let fitted: { output: IProjectionOutput; k: number } | null = null;
    for (let step: number = 0; step < DETAIL_FIT_STEPS; step++) {
        const k: number = (low + high) / 2;
        const output: IProjectionOutput = at(k);
        const fit: number = output.pxPerMm ?? 0;
        if (fit > target) {
            high = k;
            continue;
        }
        if (fit > 0) {
            fitted = { output, k };
        }
        low = k;
    }
    return fitted;
}

/**
 * A detail drawn at a standard scale: the largest standard scale not above the scale it would
 * take filling the whole cell, reached by shrinking the pane it is fitted to, and centred in the
 * cell under the title row. A scheme without a millimetre scale fills the cell as it is.
 */
function fitDetail(spec: IDetailSpec, cell: IPaneRect): { projection: IProjectionOutput; denominator: number | null } {
    const innerW: number = cell.w;
    const innerH: number = Math.max(0, cell.h - SHEET_TITLE_BAND);
    const at: (k: number) => IProjectionOutput = (k: number): IProjectionOutput =>
        spec.project({ x: 0, y: 0, w: innerW * k, h: innerH * k });
    const full: IProjectionOutput = at(1);
    const unscaled: { projection: IProjectionOutput; denominator: null } = {
        projection: translateProjection(full, 0, SHEET_TITLE_BAND),
        denominator: null,
    };
    // No millimetre scale, or a pane too small for the drawing (a fit at or below zero).
    if ((full.pxPerMm ?? 0) <= 0) {
        return unscaled;
    }
    const denominator: number = standardDenominator(full.pxPerMm ?? 0);
    const target: number = pxPerMmOf(denominator);
    const fitted: { output: IProjectionOutput; k: number } | null = searchStandardFit(at, target);
    if (!fitted) {
        return unscaled;
    }
    // A projection whose fit moves in steps may land short of the scale; one that misses it by
    // more than half a percent is not labelled with it.
    const reached: boolean = Math.abs((fitted.output.pxPerMm ?? 0) / target - 1) <= SCALE_TOLERANCE;
    const dx: number = (innerW - innerW * fitted.k) / 2;
    const dy: number = SHEET_TITLE_BAND + (innerH - innerH * fitted.k) / 2;
    return { projection: translateProjection(fitted.output, dx, dy), denominator: reached ? denominator : null };
}

/** The detail views of a turning stair: the landing or the winder schemes after the two templates. */
function turnDetailSpecs(
    result: IStairCalculationResult,
    config: IStairConfig,
    measurer: ITextMeasurer,
    common: IDetailSpec[]
): IDetailSpec[] {
    if (config.turnType === 'landing') {
        return [
            ...common,
            { id: EPaneId.LandingScheme, project: (rect: IPaneRect) => projectLandingScheme(result, config, rect, measurer) },
            { id: EPaneId.StepsSchema, project: (rect: IPaneRect) => projectStepsSchema(result, config, rect, measurer) },
        ];
    }
    return [
        ...common,
        { id: EPaneId.WinderCombined, project: (rect: IPaneRect) => projectWinderCombined(result, rect, measurer) },
        { id: EPaneId.WinderNumbered, project: (rect: IPaneRect) => projectWinderNumbered(result, config, rect) },
    ];
}

/** The detail views of this stair type, in their fixed reading order. */
function detailSpecs(result: IStairCalculationResult, config: IStairConfig, measurer: ITextMeasurer): IDetailSpec[] {
    if (config.type === 'spiral') {
        return [
            { id: EPaneId.StepDetail, project: (rect: IPaneRect) => projectSpiralStepDetail(result, config, rect, measurer) },
            { id: EPaneId.SpiralRotation, project: (rect: IPaneRect) => projectSpiralRotation(result, config, rect) },
        ];
    }
    const common: IDetailSpec[] = [
        { id: EPaneId.Stringer, project: (rect: IPaneRect) => projectStringer(result, config, rect, measurer) },
        { id: EPaneId.StepDetail, project: (rect: IPaneRect) => projectStepDetail(result, config, rect, measurer) },
    ];
    if (config.type === 'straight') {
        return [...common, { id: EPaneId.StepsSchema, project: (rect: IPaneRect) => projectStepsSchema(result, config, rect, measurer) }];
    }
    return turnDetailSpecs(result, config, measurer, common);
}

/** The detail panes, row by row in the grid of the zone. */
function detailPanes(
    specs: readonly IDetailSpec[],
    zone: IPaneRect,
    grid: IDetailGrid,
    config: IStairConfig,
    measurer: ITextMeasurer
): IPane[] {
    return specs.map((spec: IDetailSpec, index: number): IPane => {
        const col: number = index % grid.cols;
        const row: number = Math.floor(index / grid.cols);
        // A sheet too small for any grid keeps the plain reading order.
        const rect: IPaneRect = grid.cells.at(index) ?? {
            x: Math.round(zone.x + col * (grid.cellW + CELL_GAP)),
            y: Math.round(zone.y + row * (grid.cellH + CELL_GAP)),
            w: Math.max(1, Math.floor(grid.cellW)),
            h: Math.max(1, Math.floor(grid.cellH)),
        };
        const fitted: { projection: IProjectionOutput; denominator: number | null } = fitDetail(spec, rect);
        return {
            rect,
            id: spec.id,
            title: paneTitle(spec.id, config),
            scale: fitted.denominator === null ? null : scaleText(fitted.denominator),
            role: EPaneRole.Detail,
            // Labels are kept apart inside the cell, under its title row.
            projection: declutterLabels(fitted.projection, measurer, { minX: 0, minY: SHEET_TITLE_BAND, maxX: rect.w, maxY: rect.h }),
        };
    });
}

// ─── The sheet ────────────────────────────────────────────

function frameOf(size: { width: number; height: number }): IPaneRect {
    return {
        x: SHEET_MARGIN,
        y: SHEET_MARGIN,
        w: Math.max(0, size.width - 2 * SHEET_MARGIN),
        h: Math.max(0, size.height - 2 * SHEET_MARGIN),
    };
}

function contentOf(frame: IPaneRect): IPaneRect {
    return {
        x: frame.x + FRAME_PAD,
        y: frame.y + FRAME_PAD,
        w: Math.max(0, frame.w - 2 * FRAME_PAD),
        h: Math.max(0, frame.h - 2 * FRAME_PAD),
    };
}

/** The group views as panes, moved to `at` on the canvas. */
function groupPanes(plan: IGroupPlan, at: { x: number; y: number }, denominator: number, config: IStairConfig): IPane[] {
    return plan.views.map((view: IPlacedView): IPane => ({
        id: view.id,
        title: paneTitle(view.id, config),
        scale: scaleText(denominator),
        role: EPaneRole.Projection,
        rect: { ...view.rect, x: view.rect.x + at.x, y: view.rect.y + at.y },
        projection: translateProjection(view.projection, 0, 0),
    }));
}

/** The scales the group tries, largest first. */
function groupDenominators(): number[] {
    return [...STANDARD_DENOMINATORS.filter((denominator: number) => denominator >= GROUP_MIN_DENOMINATOR), 2000, 5000];
}

/**
 * How large the details come out in a grid: the logarithm of the pixels per millimetre of the
 * smallest-drawn detail filling its cell — the sheet reads as well as its least readable detail.
 * Only the details with a millimetre scale (`scaled`) count, and one that cannot be drawn in its
 * cell at all makes the grid unusable (-Infinity); a sheet of unscaled details only is judged by
 * the cell size.
 */
function detailQuality(specs: readonly IDetailSpec[], scaled: readonly boolean[], grid: IDetailGrid): number {
    const inner: IPaneRect = { x: 0, y: 0, w: grid.cellW, h: Math.max(0, grid.cellH - SHEET_TITLE_BAND) };
    const fits: number[] = specs
        .filter((_spec: IDetailSpec, index: number) => scaled[index])
        .map((spec: IDetailSpec) => spec.project(inner).pxPerMm ?? 0);
    if (fits.length === 0) {
        return Math.log(Math.max(0, gridSize(grid)));
    }
    return Math.log(Math.max(0, Math.min(...fits)));
}

/** One way to lay out the sheet, with how large it draws everything. */
interface IScoredSheet {
    plan: IGroupPlan;
    candidate: ISheetCandidate;
    denominator: number;
    score: number;
}

/**
 * The best layout over the largest group scales that leave the details their cells: a step down
 * in the group's scale is taken when the smallest detail grows more than the group shrinks. The
 * group and the details weigh the same — the score adds the logarithm of the group's pixels per
 * millimetre to that of the smallest detail. Null when no scale fits.
 */
function bestLayout(
    group: IProjectionGroup,
    content: IPaneRect,
    specs: readonly IDetailSpec[],
    config: IStairConfig,
    measurer: ITextMeasurer
): IScoredSheet | null {
    const scaled: boolean[] = specs.map((spec: IDetailSpec) => (spec.project(NOMINAL_PANE).pxPerMm ?? 0) > 0);
    let best: IScoredSheet | null = null;
    let fittingScales: number = 0;
    for (const denominator of groupDenominators()) {
        const plan: IGroupPlan = planGroup(group, denominator, config, measurer);
        const fitting: ISheetCandidate[] =
            plan.width <= content.w && plan.height <= content.h
                ? candidates(plan, content, specs.length).filter((candidate: ISheetCandidate) => gridFits(candidate.grid))
                : [];
        for (const candidate of fitting) {
            const score: number = Math.log(pxPerMmOf(denominator)) + detailQuality(specs, scaled, candidate.grid);
            if (Number.isFinite(score) && (!best || score > best.score)) {
                best = { plan, candidate, denominator, score };
            }
        }
        fittingScales += fitting.length > 0 ? 1 : 0;
        if (fittingScales >= SCALE_LOOKAHEAD) {
            break;
        }
    }
    return best;
}

/**
 * The sheet layout: the best one whose detail cells keep their minimum; on a sheet too small for
 * any, the smallest scale with the details in the band below.
 */
function chooseSheet(
    group: IProjectionGroup,
    content: IPaneRect,
    specs: readonly IDetailSpec[],
    config: IStairConfig,
    measurer: ITextMeasurer
): { plan: IGroupPlan; candidate: ISheetCandidate; denominator: number } {
    const best: IScoredSheet | null = bestLayout(group, content, specs, config, measurer);
    if (best) {
        return best;
    }
    const denominator: number = groupDenominators().at(-1) ?? 5000;
    const plan: IGroupPlan = planGroup(group, denominator, config, measurer);
    return { plan, denominator, candidate: candidates(plan, content, specs.length)[1] };
}

/**
 * The sheet: the projection group at the largest standard scale that still leaves the detail
 * cells their minimum, and the details in the best of the three zones at that scale. `views`
 * picks the group: all three views for the DXF file (the default), the top view alone for the
 * screen «План» position. Both lay it out on the one fixed sheet `SHEET_SIZE`; a canvas only
 * shows it.
 */
export function buildSheetLayout(
    result: IStairCalculationResult,
    config: IStairConfig,
    size: { width: number; height: number },
    measurer: ITextMeasurer,
    views: ESheetViews = ESheetViews.All
): ISheet {
    const frame: IPaneRect = frameOf(size);
    const content: IPaneRect = contentOf(frame);
    const group: IProjectionGroup = projectionGroup(result, config, measurer, { views, sideOnly: false });
    const specs: IDetailSpec[] = detailSpecs(result, config, measurer);
    const { plan, candidate, denominator } = chooseSheet(group, content, specs, config, measurer);
    return {
        frame,
        panes: [
            ...groupPanes(plan, candidate.groupAt, denominator, config),
            ...detailPanes(specs, candidate.zone, candidate.grid, config, measurer),
        ],
        detailZone: candidate.zone,
    };
}

/**
 * The panes of the full sheet as data: which views this stair type has, where each one sits,
 * what it is called and what it draws. The DXF writer reads it; the screen «План» position
 * reads the same assembly with the top view only, so nobody lays the panes out twice.
 */
export function buildSheet(
    result: IStairCalculationResult,
    config: IStairConfig,
    size: { width: number; height: number },
    measurer: ITextMeasurer
): IPane[] {
    return buildSheetLayout(result, config, size, measurer).panes;
}

function rowWidth(row: IPlacedRow): number {
    return row.side.rect.x + row.side.rect.w - row.front.rect.x;
}

/** The two elevations of the «Фасады» position as a group plan at one scale. */
function sideRowPlan(group: IProjectionGroup, denominator: number, config: IStairConfig, measurer: ITextMeasurer): IGroupPlan {
    const scale: string = scaleText(denominator);
    const titles: { front: number; side: number } = {
        front: titleWidth(paneTitle(EPaneId.FrontView, config), scale, measurer),
        side: titleWidth(paneTitle(EPaneId.SideView, config), scale, measurer),
    };
    const row: IPlacedRow = planRow(group, pxPerMmOf(denominator), titles, 0, measurer);
    const height: number = row.bottom - row.top;
    return {
        width: rowWidth(row),
        views: [row.front, row.side].map((view: IPlacedView) => shiftView(view, -row.front.rect.x, -row.top)),
        rowBottom: height,
        topRight: 0,
        height,
    };
}

/**
 * The «Фасады» position: the front and the side view on one floor line at one standard scale —
 * the largest that fits the sheet — with the frame drawn close around them, so a canvas showing
 * it shows the elevations as large as it can.
 */
export function buildSideSheetLayout(
    result: IStairCalculationResult,
    config: IStairConfig,
    size: { width: number; height: number },
    measurer: ITextMeasurer
): ISheet {
    const content: IPaneRect = contentOf(frameOf(size));
    const group: IProjectionGroup = projectionGroup(result, config, measurer, { sideOnly: true, views: ESheetViews.All });
    let chosen: { plan: IGroupPlan; denominator: number } | null = null;
    for (const denominator of groupDenominators()) {
        chosen = { plan: sideRowPlan(group, denominator, config, measurer), denominator };
        if (chosen.plan.width <= content.w && chosen.plan.height <= content.h) {
            break;
        }
    }
    if (!chosen) {
        return { frame: frameOf(size), panes: [], detailZone: null };
    }
    const at: { x: number; y: number } = { x: content.x, y: content.y };
    const frame: IPaneRect = {
        x: content.x - FRAME_PAD,
        y: content.y - FRAME_PAD,
        w: chosen.plan.width + 2 * FRAME_PAD,
        h: chosen.plan.height + 2 * FRAME_PAD,
    };
    return { frame, panes: groupPanes(chosen.plan, at, chosen.denominator, config), detailZone: null };
}

/** The panes of the «Фасады» canvas position. */
export function buildSideSheet(
    result: IStairCalculationResult,
    config: IStairConfig,
    size: { width: number; height: number },
    measurer: ITextMeasurer
): IPane[] {
    return buildSideSheetLayout(result, config, size, measurer).panes;
}
